import { useCallback, useState } from 'react';

import { useEntitySchema, useStack9 } from '@april9au/stack9-react';
import { ep } from '@april9au/stack9-sdk';
import * as ui from '@april9au/stack9-ui';

import { entityNames } from '../../constants/entities';
import { Dependency } from '../../models/stack9/Dependency';
import OptionSetDropdown from './OptionSetDropDown';
import RequirementDropdown from './RequirementDropdown';

const { useScreen } = ui;
const { useForm } = ui.S9Form;
const S9Form = ui.S9Form.default;
const S9EntityActions = ui.S9EntityActions.default;
const S9Col = ui.S9Col.default;
const S9Row = ui.S9Row.default;
const S9Drawer = ui.S9Drawer.default;

type DependencyDrawerProps = {
  countryTemplateId: number;
  onClose: () => void;
  id?: number;
  initialValues?: Partial<Dependency>;
};

export const DependencyForm = ({
  countryTemplateId,
  onClose,
  id,
  initialValues,
}: DependencyDrawerProps) => {
  const { queryService } = useStack9();

  const { screen } = useScreen();

  const screenKey = ep(screen && screen.head.key);
  const { schema, isValidating } = useEntitySchema(entityNames.DEPENDENCY);
  const [form] = useForm();

  const [requirement, setRequirement] = useState(
    initialValues?.requirement_id ?? undefined,
  );
  const [optionSet, setOptionSet] = useState(
    initialValues?.option_set_id ?? undefined,
  );

  const handleSave = useCallback(async () => {
    const query = id ? 'updateDependency' : 'createDependency';
    await queryService.runNamedQuery(screenKey, query, {
      vars: {
        requirement_id: requirement,
        option_set_id: optionSet,
        id,
      },
    });
    setRequirement(undefined);
    setOptionSet(undefined);
    onClose?.();
  }, [id, onClose, optionSet, queryService, requirement, screenKey]);

  return (
    <S9Form
      isValidating={isValidating}
      onFinish={handleSave}
      loading={isValidating}
      form={form}
      initialValues={initialValues}
    >
      <S9Drawer.Header
        title={id ? 'Edit dependency' : 'Create dependency'}
        showCancel
        extra={
          <S9EntityActions
            entityKey={entityNames.DEPENDENCY}
            onDeleted={() => {
              setRequirement(undefined);
              setOptionSet(undefined);
              onClose?.();
            }}
            entityId={id}
            form={form}
          />
        }
      />
      <S9Drawer.Content>
        {schema && (
          <>
            <S9Row gutter={24}>
              <S9Col md={24} style={{ marginBottom: '20px' }}>
                <RequirementDropdown
                  setRequirement={setRequirement}
                  countryTemplateId={countryTemplateId}
                  requirement={requirement}
                />
              </S9Col>
              <S9Col md={24}>
                {requirement && (
                  <OptionSetDropdown
                    setOptionSet={setOptionSet}
                    requirement={requirement}
                    optionSet={optionSet}
                  />
                )}
              </S9Col>
            </S9Row>
          </>
        )}
      </S9Drawer.Content>
    </S9Form>
  );
};
